import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetDailySummariesQuery,
  useDeleteDailySummaryMutation,
  useImportDailySummaryMutation,
} from '../../store/api';
import { extractList, extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { DAILY_SUMMARY_CREATE_PATH } from '../../navigations/path';
import SweetAlert from '../../utils/sweetAlert';
import Can from '../../components/permissions/Can';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

const DailySummaryList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [assignNumberFilter, setAssignNumberFilter] = useState('');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuItem, setActionMenuItem] = useState(null);
  const fileInputRef = useRef(null);

  const queryParams = useMemo(() => {
    const params = {
      // Only the agent name is rendered here; assignedNumber is used on the view page
      'relations[]': ['agent'],
      paginate: 1,
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (search) params.search = search;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (assignNumberFilter) params.assigned_number_id = assignNumberFilter;
    return params;
  }, [page, rowsPerPage, search, dateFrom, dateTo, assignNumberFilter]);

  const { data, isFetching: fetching, error, refetch } = useGetDailySummariesQuery(queryParams);
  const [deleteDailySummary] = useDeleteDailySummaryMutation();
  const [importDailySummary, { isLoading: importing }] = useImportDailySummaryMutation();
  const loading = fetching || importing;

  const list = useMemo(() => {
    const rawList = data?.data?.list ?? data?.list ?? extractList(data);
    return Array.isArray(rawList) ? rawList : [];
  }, [data]);

  const total = useMemo(() => {
    const pag = data?.data?.pagination ?? data?.pagination ?? extractPagination(data);
    return pag.total ?? 0;
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message || 'Failed to load daily summaries');
  }, [error]);

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await importDailySummary(formData).unwrap();
      toast.success('Daily summaries imported successfully.');
      setPage(0);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to import file');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSearch = () => setSearch(searchInput);
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const getItemId = (item) => item?.id ?? item?.uuid ?? '';

  const handleView = (item) => {
    const id = getItemId(item);
    if (id) navigate(`/daily-summary/view/${id}`);
  };

  const handleEdit = (item) => {
    const id = getItemId(item);
    if (id) navigate(`/daily-summary/edit/${id}`);
  };

  const handleDelete = async (item) => {
    const id = getItemId(item);
    if (!id) return;
    const result = await SweetAlert.deleteConfirm('this daily summary');
    if (!result?.isConfirmed) return;
    try {
      await deleteDailySummary(id).unwrap();
      toast.success('Daily summary deleted.');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete daily summary');
    }
  };

  const openActionMenu = (event, item) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuItem(item);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuItem(null);
  };

  const handleMenuView = () => {
    if (actionMenuItem) handleView(actionMenuItem);
    closeActionMenu();
  };

  const handleMenuEdit = () => {
    if (actionMenuItem) handleEdit(actionMenuItem);
    closeActionMenu();
  };

  const handleMenuDelete = () => {
    if (actionMenuItem) handleDelete(actionMenuItem);
    closeActionMenu();
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Call Counters ({list.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Can permission="agentCallReport-create">
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept=".xls,.xlsx,.csv"
                  onChange={handleImportFileChange}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleImportClick}
                >
                  Import Excel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(DAILY_SUMMARY_CREATE_PATH)}
                >
                  Add Daily Summary
                </Button>
              </>
            </Can>
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              label="Date from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, display: 'none' }}
            />
            <TextField
              size="small"
              label="Date to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 , display: 'none'}}
            />
            <FormControl size="small" sx={{ minWidth: 200,display: 'none' }}>
              <InputLabel>Dial From / Assign Number</InputLabel>
              <Select
                value={assignNumberFilter}
                label="Dial From / Assign Number"
                onChange={(e) => setAssignNumberFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {/* {assignNumberOptions.map((opt) => (
                  <MenuItem key={opt.id ?? opt.uuid} value={opt.id ?? opt.uuid}>
                    {opt.name ?? opt.number ?? opt.label ?? opt.id}
                  </MenuItem>
                ))} */}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={handleSearch}>
              Search
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setDateFrom('');
                setDateTo('');
                setAssignNumberFilter('');
                setPage(0);
              }}
            >
              Clear
            </Button>
          </Box>
        </Paper>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent Name</TableCell>
                <TableCell>No. of Dials</TableCell>
                <TableCell align="right">Answered Calls</TableCell>
                <TableCell align="right">Call Duration</TableCell>
                <TableCell align="right">Interested Leads (&gt; 1 Mint)</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Dial From / Assign Number</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(rowsPerPage)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={8}><Skeleton height={40} /></TableCell>
                  </TableRow>
                ))
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    No daily summaries found.
                  </TableCell>
                </TableRow>
              ) : (
                list.map((item) => {
                  const id = getItemId(item);
                  // const isDeleting = deletingId === id;
                  return (
                    <TableRow key={id}>
                      <TableCell>{item.agent?.name ?? '—'}</TableCell>
                      <TableCell>{item.no_of_dials}</TableCell>
                      <TableCell align="right">{item.answered_calls}</TableCell>
                      <TableCell align="right">{item.call_duration}</TableCell>
                      <TableCell align="right">{item.interested_leads}</TableCell>
                      <TableCell>{formatDate(item.report_date)}</TableCell>
                      <TableCell>{item.assign_number}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          aria-label="Actions"
                          onClick={(e) => openActionMenu(e, item)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={closeActionMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 120 } }}
        >
          <Can permission="agentCallReport-view">
            <MenuItem onClick={handleMenuView}>
              <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
              <ListItemText>View</ListItemText>
            </MenuItem>
          </Can>
          <Can permission="agentCallReport-edit">
            <MenuItem onClick={handleMenuEdit}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          </Can>
          <Can permission="agentCallReport-delete">
            <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Can>
        </Menu>

        {!loading && list.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page"
          />
        )}

      </Container>
    </PageLayout>
  );
};

export default DailySummaryList;
